import { type Dispatch, type FC, type ReactNode, type SetStateAction } from 'react';

import { Overview } from '@eneco/flows/components';
import { useFormatter } from '@eneco/i18n';
import { Bleed, Box } from '@eneco/sparky';
import type { DC_Repositories_Base_Enumerations_Label } from '@monorepo-types/dc';

import type { SummaryProductsOverviewTextualData } from './summary-step.types';
import type { FlowConsumerContextEnergy } from '../../../context';
import { getCashbackTexts } from '../../helpers';
import type { Offer } from '../../types';
import { OfferSection } from '../ProductCard/ExpandableOfferSection';
import { Ribbon } from '../Ribbon/Ribbon';

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  textualData?: any;
  offer: Offer;
  flowContext: FlowConsumerContextEnergy;
  textualDataKey: string;
  setCostPeriod?: Dispatch<SetStateAction<string>>;
  selectedCashbackTexts?: NonNullable<ReturnType<typeof getCashbackTexts>> | null;
  cashbackValue?: number | string;
  cashbackType?: string;
  ribbonTheme?: DC_Repositories_Base_Enumerations_Label;
  priceDetails: ReactNode;
  wrapPriceDetails?: boolean;
  productsOverview?: SummaryProductsOverviewTextualData;
}

export const SummaryProductsOverview: FC<Props> = ({
  textualData,
  offer,
  flowContext,
  textualDataKey,
  setCostPeriod,
  selectedCashbackTexts,
  cashbackValue = 0,
  cashbackType,
  ribbonTheme,
  priceDetails,
  wrapPriceDetails = false,
  productsOverview = textualData?.productsOverview,
}) => {
  const { currency, format } = useFormatter();

  return (
    <Overview>
      <Overview.Header>{productsOverview?.title?.value}</Overview.Header>

      {selectedCashbackTexts && (
        <Bleed top="6">
          <Ribbon
            theme={ribbonTheme}
            size="large"
            emphasis="medium"
            title={format(selectedCashbackTexts.title, {
              [`${cashbackType}`]: currency.euroNoFractionDigits(Number(cashbackValue) || 0),
            })}
            text={selectedCashbackTexts?.text}
            trigger={selectedCashbackTexts?.trigger}
          />
        </Bleed>
      )}

      <Box paddingTop={3}>
        <OfferSection
          offer={offer}
          textualData={textualData}
          flowContext={flowContext}
          textualDataKey={textualDataKey}
          setCostPeriod={setCostPeriod}
          contractStartDateLabel={productsOverview?.contractStartDateLabel?.value}
          contractEndDateLabel={productsOverview?.contractEndDateLabel?.value}
          showLegalText={false}>
          {wrapPriceDetails ? (
            <Box backgroundColor="backgroundSecondary" paddingX="3">
              {priceDetails}
            </Box>
          ) : (
            priceDetails
          )}
        </OfferSection>
      </Box>
    </Overview>
  );
};
